import React, { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Sistema de Notificaciones para el Frontend
 * Compatible con StructuredMessage del backend
 *
 * Tipos de mensaje: success, error, warning, info
 * Severidad: low, medium, high, critical
 */

const Notification = ({ message, onClose }) => {
  useEffect(() => {
    const duration = message.severity === 'critical'
      ? 8000
      : message.severity === 'high'
        ? 6000
        : message.severity === 'medium'
          ? 4500
          : 3200;

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [message, onClose]);

  const config = useMemo(() => {
    switch (message.type) {
      case 'success':
        return {
          icon: 'check_circle',
          label: 'Success',
          accent: 'bg-emerald-500',
          iconWrap: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
          surface: 'bg-white dark:bg-[#14212d]',
          border: 'border-emerald-200 dark:border-emerald-900/40',
          text: 'text-emerald-900 dark:text-emerald-50',
          muted: 'text-emerald-700/90 dark:text-emerald-100/80',
          details: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-100 dark:border-emerald-900/30',
        };
      case 'error':
        return {
          icon: 'error',
          label: 'Danger',
          accent: 'bg-rose-500',
          iconWrap: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
          surface: 'bg-white dark:bg-[#14212d]',
          border: 'border-rose-200 dark:border-rose-900/40',
          text: 'text-rose-950 dark:text-rose-50',
          muted: 'text-rose-700/90 dark:text-rose-100/80',
          details: 'bg-rose-50 dark:bg-rose-950/30 border-rose-100 dark:border-rose-900/30',
        };
      case 'warning':
        return {
          icon: 'warning',
          label: 'Warning',
          accent: 'bg-amber-500',
          iconWrap: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
          surface: 'bg-white dark:bg-[#14212d]',
          border: 'border-amber-200 dark:border-amber-900/40',
          text: 'text-amber-950 dark:text-amber-50',
          muted: 'text-amber-800/90 dark:text-amber-100/80',
          details: 'bg-amber-50 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900/30',
        };
      case 'info':
      default:
        return {
          icon: 'info',
          label: 'Info',
          accent: 'bg-sky-500',
          iconWrap: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
          surface: 'bg-white dark:bg-[#14212d]',
          border: 'border-sky-200 dark:border-sky-900/40',
          text: 'text-sky-950 dark:text-sky-50',
          muted: 'text-sky-700/90 dark:text-sky-100/80',
          details: 'bg-sky-50 dark:bg-sky-950/30 border-sky-100 dark:border-sky-900/30',
        };
    }
  }, [message.type]);

  const hasDetails = Boolean(
    message.field ||
    (message.details && Object.keys(message.details).length > 0)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 32, scale: 0.98 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`relative overflow-hidden rounded-2xl border shadow-[0_18px_48px_rgba(15,23,42,0.14)] ${config.surface} ${config.border} max-w-lg w-full`}
      role="status"
      aria-live="polite"
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${config.accent}`} />

      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${config.iconWrap}`}>
            <span className="material-icons-round text-[22px]">{config.icon}</span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${config.iconWrap}`}>
                {config.label}
              </span>
              {message.severity && (
                <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                  {message.severity}
                </span>
              )}
            </div>

            {message.title && (
              <h3 className={`mt-2 text-sm font-bold ${config.text}`}>
                {message.title}
              </h3>
            )}

            {message.message && (
              <p className={`mt-1 text-sm leading-6 ${config.muted}`}>
                {message.message}
              </p>
            )}

            {hasDetails && (
              <div className={`mt-3 rounded-xl border p-3 ${config.details}`}>
                {message.field && (
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                    Campo:
                    <span className="ml-1 font-mono text-slate-900 dark:text-white">
                      {message.field}
                    </span>
                  </p>
                )}

                {message.details && Object.keys(message.details).length > 0 && (
                  <details className={message.field ? 'mt-2' : ''}>
                    <summary className="cursor-pointer text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
                      Ver detalles
                    </summary>
                    <pre className="mt-2 overflow-auto rounded-xl bg-slate-950 px-3 py-2 text-xs text-slate-100 max-h-40">
                      {JSON.stringify(message.details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Cerrar notificación"
          >
            <span className="material-icons-round text-lg">close</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const NotificationContainer = ({ notifications, removeNotification }) => {
  return (
    <div className="fixed right-4 top-4 z-[80] w-[calc(100vw-2rem)] max-w-lg space-y-3 pointer-events-none sm:right-6 sm:top-6">
      <AnimatePresence>
        {notifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <Notification
              message={notification}
              onClose={() => removeNotification(notification.id)}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Notification;
